import axios from 'axios';
import environment from '../../config/environment';

// Define the interface locally instead of importing it
interface Form990QuestionnaireSubmission {
  fiscalSponsorId: string;
  responses: Record<string, any>;
  submittedBy: string;
  status?: string;
  [key: string]: any; // Add index signature to allow string indexing
}

const env = environment();

// Create a single axios instance with environment-aware configuration
export const apiInstance = axios.create({
  baseURL: `${env.apiUrl}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Single request interceptor for auth
apiInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single response interceptor for error handling
apiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      environment: env.nodeEnv
    });

    if (error.response?.status === 401) {
      console.warn('Authentication failed - redirecting to login');
      localStorage.removeItem('authToken');
      window.location.href = '/portal';
    }
    return Promise.reject(error);
  }
);

// Define submission status type
export type SubmissionStatus = {
  success: boolean;
  message: string;
  id?: string;
  timestamp?: string;
  status?: 'DRAFT' | 'SUBMITTED' | 'ERROR';
  errors?: Form990ValidationError[];
};

// Validation interface
interface Form990ValidationError {
  field: string;
  message: string;
}

// Transform responses to match expected server format
const transformResponses = (data: Form990QuestionnaireSubmission) => {
  const { responses } = data;
  
  return {
    organizationInfo: {
      activitiesAndPurpose: responses.part3_q8 || 'Default organization activities and purpose',
      significantChanges: responses.part3_q1 ?? false,
      newPrograms: responses.part3_q6 ?? false,
      discontinuedOperations: responses.part3_q7 ?? false,
      employeesCount: Number(responses.part3_q2) || 0,
      volunteersCount: Number(responses.part3_q3) || 0,
      totalRevenue: Number(responses.part3_q4) || 0,
      totalExpenses: Number(responses.part3_q5) || 0
    },
    governance: {
      boardMeetingDocumentation: responses.part4_q1 ?? false,
      committeesWithAuthority: responses.part4_q2 ?? false,
      significantDiversion: responses.part4_q3 ?? false,
      conflictOfInterestPolicy: responses.part4_q4 ?? false,
      whistleblowerPolicy: responses.part4_q5 ?? false,
      documentRetentionPolicy: responses.part4_q6 ?? false
    },
    compensation: {
      compensationProcess: responses.part5_q1 ?? false,
      independentReview: responses.part5_q2 ?? false,
      compensationSources: responses.part5_q3 || []
    },
    financialPractices: {
      auditedFinancials: responses.part6_q1 ?? false,
      auditCommittee: responses.part6_q2 ?? false,
      federalGrantRequirements: responses.part6_q3 ?? false
    }
  };
};

const validateQuestionnaireData = (data: Form990QuestionnaireSubmission): Form990ValidationError[] => {
  const errors: Form990ValidationError[] = [];
  const transformedData = transformResponses(data);

  // Check required fields in transformed format
  if (!transformedData.organizationInfo.activitiesAndPurpose) {
    errors.push({
      field: 'organizationInfo.activitiesAndPurpose',
      message: 'Activities and purpose is required'
    });
  }

  // Validate base required fields
  const requiredFields = [
    'fiscalSponsorId',
    'submittedBy',
    'status'
  ];

  requiredFields.forEach(field => {
    if (!data[field]) {
      errors.push({
        field,
        message: `${field} is required`
      });
    }
  });

  return errors;
};

// Update form990API to use the single apiInstance
export const form990API = {
  submitQuestionnaire: async (data: Form990QuestionnaireSubmission) => {
    try {
      console.log('🔍 Debug - Submitting questionnaire data:', data);
      
      const response = await apiInstance.post('/sponsfi/form990/questionnaire', data);
      
      console.log('🔍 Debug - Submission response:', response.data);
      
      return {
        success: true,
        data: response.data,
        message: 'Questionnaire submitted successfully'
      };
    } catch (error: any) {
      console.error('Submission error details:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to submit questionnaire',
        error: error
      };
    }
  },

  verifySubmission: async (submissionId: string): Promise<boolean> => {
    try {
      const response = await apiInstance.get(`/sponsfi/form990/questionnaire/${submissionId}`);
      return response.data?.status === 'SUBMITTED';
    } catch (error) {
      console.error('Failed to verify submission:', error);
      return false;
    }
  },

  getDrafts: async () => {
    try {
      const response = await apiInstance.get('/sponsfi/form990/draft');
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch Form 990 drafts:', error);
      throw error;
    }
  },

  generateForm: async (params: any) => {
    const response = await apiInstance.post('/sponsfi/form990/generate', params);
    return response.data;
  },

  getLatestQuestionnaire: async (fiscalSponsorId: string) => {
    try {
      console.log('Fetching latest questionnaire for:', fiscalSponsorId);
      
      const response = await apiInstance.get(
        `/sponsfi/form990/questionnaire/${fiscalSponsorId}/latest`
      );
      
      console.log('Received questionnaire data:', response.data);
      
      return {
        success: true,
        data: response.data,
        message: 'Successfully retrieved latest questionnaire'
      };
    } catch (error: any) {
      console.error('Failed to fetch latest questionnaire:', {
        fiscalSponsorId,
        error: error.message,
        status: error.response?.status,
        url: `/sponsfi/form990/questionnaire/${fiscalSponsorId}/latest`
      });
      
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch latest questionnaire',
        error: error
      };
    }
  }
};

export const updateForm990Data = async (fiscalSponsorId: string, updates: any) => {
  try {
    const response = await axios.patch(
      `/api/fiscal-sponsors/${fiscalSponsorId}/form990-data`,
      updates
    );
    return response.data;
  } catch (error) {
    console.error('Error updating Form 990 data:', error);
    throw error;
  }
};

// Export the API
export const api = {
  form990: form990API,
  
  // Add other API services here
  fiscalSponsor: {
    getFiscalSponsor: async () => {
      try {
        const response = await apiInstance.get('/fiscal-sponsor');
        return response.data;
      } catch (error: any) {
        return {
          success: false,
          message: error.response?.data?.message || error.message,
          error: error
        };
      }
    },
    
    createFiscalSponsor: async (data: any) => {
      try {
        const response = await apiInstance.post('/fiscal-sponsor', data);
        return response.data;
      } catch (error: any) {
        return {
          success: false,
          message: error.response?.data?.message || error.message,
          error: error
        };
      }
    },
    
    updateFiscalSponsor: async (data: any) => {
      try {
        const response = await apiInstance.put('/fiscal-sponsor', data);
        return response.data;
      } catch (error: any) {
        return {
          success: false,
          message: error.response?.data?.message || error.message,
          error: error
        };
      }
    }
  }
};

export default apiInstance; 